// src/audio/pcm.ts
// Звук в браузере для страницы робота:
//  - MicCapture: микрофон -> PCM int16 16 кГц моно порциями по 100 мс
//    (так робот принимает голос с сайта: robot/<id>/audio/voice)
//  - PcmPlayer: PCM int16 16 кГц с робота (robot/<id>/audio/live) -> динамики, с буфером от рывков сети
//  - playClip: готовая запись PCM целиком (тест микрофона)
//  - speak: озвучка текста голосом браузера (speechSynthesis)
// Микрофон браузер даёт только на https:// или localhost.

export const PCM_RATE = 16000;

const TAP_WORKLET = `
class PcmTap extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) this.port.postMessage(ch.slice(0));
    return true;
  }
}
registerProcessor('pcm-tap', PcmTap);
`;

export class MicCapture {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;
  private acc: number[] = [];
  private pos = 0;
  private out: number[] = [];
  private ratio = 3;

  constructor(
    private onChunk: (pcm: Int16Array) => void,
    private onLevel?: (rms: number) => void,
    private chunkSamples = PCM_RATE / 10,
  ) {}

  static supported(): boolean {
    return !!(navigator.mediaDevices && window.isSecureContext && typeof AudioWorkletNode !== 'undefined');
  }

  async start() {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.ratio = ctx.sampleRate / PCM_RATE;
    const url = URL.createObjectURL(new Blob([TAP_WORKLET], { type: 'application/javascript' }));
    try {
      await ctx.audioWorklet.addModule(url);
    } finally {
      URL.revokeObjectURL(url);
    }
    const src = ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(ctx, 'pcm-tap');
    this.node.port.onmessage = e => this.push(e.data as Float32Array);
    // Узел должен быть в графе до destination, иначе браузер его не вызывает; звук глушим
    const mute = ctx.createGain();
    mute.gain.value = 0;
    src.connect(this.node).connect(mute).connect(ctx.destination);
  }

  // Понижение частоты: среднее по окну ratio входных отсчётов (заодно фильтр НЧ)
  private push(input: Float32Array) {
    for (let i = 0; i < input.length; i++) this.acc.push(input[i]);
    let sumSq = 0;
    let n = 0;
    while (this.pos + this.ratio <= this.acc.length) {
      const a = Math.floor(this.pos);
      const b = Math.max(a + 1, Math.floor(this.pos + this.ratio));
      let s = 0;
      for (let i = a; i < b; i++) s += this.acc[i];
      const v = s / (b - a);
      this.out.push(v);
      sumSq += v * v;
      n++;
      this.pos += this.ratio;
    }
    const drop = Math.floor(this.pos);
    this.acc.splice(0, drop);
    this.pos -= drop;
    if (n && this.onLevel) this.onLevel(Math.sqrt(sumSq / n));
    while (this.out.length >= this.chunkSamples) {
      this.onChunk(floatToInt16(this.out.splice(0, this.chunkSamples)));
    }
  }

  // Останавливает запись; возвращает недосланный хвост
  stop(): Int16Array {
    const tail = floatToInt16(this.out);
    this.out = [];
    this.acc = [];
    this.pos = 0;
    this.node?.port.close();
    this.node?.disconnect();
    this.stream?.getTracks().forEach(t => t.stop());
    this.ctx?.close().catch(() => {});
    this.node = null;
    this.stream = null;
    this.ctx = null;
    return tail;
  }
}

function floatToInt16(samples: ArrayLike<number>): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const v = Math.max(-1, Math.min(1, samples[i]));
    out[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  return out;
}

export function int16ToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return btoa(bin);
}

export class PcmPlayer {
  private ctx = new AudioContext();
  private gain = this.ctx.createGain();
  private next = 0;
  level = 0;

  constructor(private latency = 0.3) {
    this.gain.connect(this.ctx.destination);
  }

  setVolume(v: number) {
    this.gain.gain.value = v;
  }

  // bytes: int16 LE моно PCM_RATE
  play(bytes: Uint8Array) {
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    const n = Math.floor(bytes.byteLength / 2);
    if (!n) return;
    const view = new DataView(bytes.buffer, bytes.byteOffset, n * 2);
    const buf = this.ctx.createBuffer(1, n, PCM_RATE);
    const ch = buf.getChannelData(0);
    let sumSq = 0;
    for (let i = 0; i < n; i++) {
      const v = view.getInt16(i * 2, true) / 32768;
      ch[i] = v;
      sumSq += v * v;
    }
    this.level = Math.sqrt(sumSq / n);
    const now = this.ctx.currentTime;
    // Опоздали (буфер кончился) или накопилась задержка — начинаем заново с запасом latency
    if (this.next < now + 0.02 || this.next > now + this.latency * 4) this.next = now + this.latency;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.gain);
    src.start(this.next);
    this.next += buf.duration;
  }

  close() {
    this.ctx.close().catch(() => {});
  }
}

// Готовая запись целиком (не живой поток): один буфер, без подрезки задержки,
// которая у PcmPlayer накладывала бы порции друг на друга. Возвращает «стоп».
export function playClip(pcm: Int16Array, volume = 1, onEnd?: () => void): () => void {
  const ctx = new AudioContext();
  const buf = ctx.createBuffer(1, Math.max(1, pcm.length), PCM_RATE);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 32768;
  const gain = ctx.createGain();
  gain.gain.value = volume;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.connect(gain).connect(ctx.destination);
  src.onended = () => {
    ctx.close().catch(() => {});
    onEnd?.();
  };
  src.start();
  ctx.resume().catch(() => {});  // некоторые браузеры создают контекст приостановленным
  return () => {
    try {
      src.stop();
    } catch {
      /* уже остановлен */
    }
  };
}

export function speechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

export function speak(text: string) {
  if (!speechSupported()) return;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = 'ru-RU';
  const voice = window.speechSynthesis.getVoices().find(v => v.lang.toLowerCase().startsWith('ru'));
  if (voice) u.voice = voice;
  window.speechSynthesis.speak(u);
}

// Распознавание речи браузером (Chrome/Edge/Safari: webkitSpeechRecognition)
export function browserRecognizer(): any | null {
  const w = window as any;
  const Rec = w.SpeechRecognition || w.webkitSpeechRecognition;
  if (!Rec) return null;
  const rec = new Rec();
  rec.lang = 'ru-RU';
  rec.interimResults = false;
  rec.maxAlternatives = 1;
  return rec;
}

// Короткий сигнал для оповещений
export function beep(freq = 880, ms = 250) {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.frequency.value = freq;
    g.gain.value = 0.15;
    osc.connect(g).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + ms / 1000);
    osc.onended = () => ctx.close().catch(() => {});
  } catch {
    /* без звука */
  }
}
